/** Monatsliste der eigenen Einträge und Formular für Eintrag/Nachtrag. */
import { api } from '../api.js';
import { newId } from '../clock.js';
import { cachedGet, currentMe, getClock, setClock, localAround, localMonth, outbox, refreshMonth, saveChange, toChange } from '../store.js';
import { $, dayLabel, errorText, esc, formValues, options, toast } from '../ui.js';
import type { LocalEntry } from '../outbox.js';
import type { EntryChange, EntryDTO, SyncResult, UserDTO } from '../../shared/types.js';
import { computeEarnings, formatEuro, type Earnings, type WageRule } from '../../shared/wage.js';
import { MILOG_DAYS, findOverlaps, validateEntry } from '../../shared/rules.js';
import {
  berlinDate, daysBetween, entryInterval, formatHM, isValidDate, isValidTime, monthLabel, monthOf, shiftMonth,
  workMinutes,
} from '../../shared/time.js';

export async function renderEntries(el: HTMLElement, month: string): Promise<void> {
  const draw = async () => {
    const { entries, locked } = await localMonth(month);
    const done = entries.filter((e) => e.end !== null);
    const total = done.reduce((s, e) => s + workMinutes(e.date, e.start, e.end!, e.pauseMin), 0);
    const me = currentMe()!;
    const target = me.user.targetHoursMonth;
    const failed = await outbox.failed();
    const byDay = new Map<string, LocalEntry[]>();
    for (const e of entries) byDay.set(e.date, [...(byDay.get(e.date) ?? []), e]);
    el.innerHTML = `
      <div class="month-nav">
        <a class="btn icon" href="#/eintraege/${shiftMonth(month, -1)}" aria-label="Vormonat">‹</a>
        <h1>${esc(monthLabel(month))}</h1>
        <a class="btn icon" href="#/eintraege/${shiftMonth(month, 1)}" aria-label="Folgemonat">›</a>
      </div>
      <div class="totals">
        <div><span class="big">${formatHM(total)}</span><span class="muted"> Std. erfasst</span></div>
        ${target ? `<div class="muted">Soll ${String(target).replace('.', ',')} Std.</div>` : ''}
        ${locked ? '<div class="badge ok">Monat freigegeben – Änderungen nur durch die Verwaltung</div>' : ''}
      </div>
      <div id="earnings"></div>
      ${failed.length ? `<div class="alert error">${failed.length === 1 ? 'Ein Eintrag wurde' : `${failed.length} Einträge wurden`} vom Server abgelehnt. Bitte öffnen und korrigieren.</div>` : ''}
      ${byDay.size ? [...byDay.entries()]
        .map(([date, list]) => `
          <div class="day"><h3>${esc(dayLabel(date))}</h3><ul class="entry-list">
          ${list.map((e) => `<li><a href="#/eintrag/${esc(e.id)}">
            <span class="t">${esc(e.start)}–${esc(e.end ?? 'läuft')}</span>
            <span class="a">${esc(e.activity)}${e.pauseMin ? `<small>${e.pauseMin} min Pause</small>` : ''}${e.note ? `<small>${esc(e.note)}</small>` : ''}</span>
            <span class="d">${e.end ? formatHM(workMinutes(e.date, e.start, e.end, e.pauseMin)) : ''}</span>
            ${e.pending ? `<span class="badge ${e.error ? 'err' : 'wait'}">${e.error ? 'Fehler' : 'wartet'}</span>` : ''}
            ${e.late ? '<span class="badge late" title="Später als 7 Tage nach dem Arbeitstag erfasst">Nachtrag</span>' : ''}
          </a></li>`).join('')}</ul></div>`)
        .join('') : '<p class="muted">Keine Einträge in diesem Monat.</p>'}
      ${locked ? '' : '<a class="btn primary fab" href="#/eintrag/neu">+ Eintrag</a>'}
    `;
  };
  const showEarnings = async () => {
    const box = document.getElementById('earnings');
    if (!box) return;
    const { entries } = await localMonth(month);
    const minutes = entries.filter((e) => e.end !== null).reduce((s, e) => s + workMinutes(e.date, e.start, e.end!, e.pauseMin), 0);
    const r = await cachedGet<{ visible: boolean; earnings: Earnings | null; rule: WageRule | null }>(`earnings:${month}`, `/earnings?month=${month}`).catch(() => null);
    box.innerHTML = r?.data ? earningsHtml(r.data, minutes, currentMe()!.user) : '';
  };
  await draw();
  void showEarnings();
  const fresh = await refreshMonth(month).catch(() => null);
  if (fresh && location.hash.startsWith('#/eintraege')) {
    await draw();
    void showEarnings();
  }
}

/** Verdienst-Karte: Stunden × Stundenlohn, lokal gerechnet (inkl. noch nicht hochgeladener Einträge). */
function earningsHtml(d: { visible: boolean; earnings: Earnings | null; rule: WageRule | null }, minutes: number, user: UserDTO): string {
  if (!d.visible || !d.earnings) return '';
  const e = computeEarnings({
    minutes, hourlyWage: d.earnings.hourlyWage, employment: user.employmentType, rule: d.rule, targetHoursMonth: user.targetHoursMonth,
  });
  const pct = e.limit ? Math.min(100, Math.round((e.gross / e.limit) * 100)) : 0;
  return `<section class="earn card ${e.exceeded ? 'over' : ''}">
    <div class="earn-row"><span>Voraussichtlicher Verdienst</span><strong>${formatEuro(e.gross)}</strong></div>
    <p class="muted small">${formatHM(minutes)} Std. × ${formatEuro(e.hourlyWage)} – brutto, vor Abrechnung. Maßgeblich ist die Lohnabrechnung.</p>
    ${e.limit !== null ? `
      <div class="meter" role="meter" aria-valuemin="0" aria-valuemax="${e.limit}" aria-valuenow="${e.gross}" aria-label="Minijob-Grenze">
        <span style="width:${pct}%"></span></div>
      <p class="small">${e.exceeded
        ? `<strong>Minijob-Grenze von ${formatEuro(e.limit)} überschritten.</strong> Bitte mit der Verwaltung sprechen.`
        : `Minijob-Grenze ${formatEuro(e.limit)}: noch ${formatEuro(e.remainingEuro!)} bzw. ca. <strong>${formatHM(e.remainingMinutes!)} Std.</strong> bis zur Grenze.`}</p>` : ''}
  </section>`;
}

export interface EntryFormOpts {
  /** Admin-Modus: speichert direkt online, darf gesperrte Monate ändern. */
  admin?: { userId: number; userName: string; activities: string[]; onDone: () => void; audit?: unknown[] };
}

async function findLocal(id: string): Promise<LocalEntry | null> {
  const today = berlinDate(Date.now());
  // In den letzten Monaten suchen (Cache)
  for (let i = 0; i < 4; i++) {
    const m = shiftMonth(monthOf(today), -i);
    const hit = (await localMonth(m)).entries.find((e) => e.id === id);
    if (hit) return hit;
  }
  const item = (await outbox.list()).find((i) => i.id === id);
  if (item) return { ...item.entry, userId: item.entry.userId ?? 0, late: false, pending: true, error: item.error };
  return null;
}

export async function renderEntryForm(el: HTMLElement, id: string | null, opts: EntryFormOpts = {}, preset?: EntryDTO): Promise<void> {
  const me = currentMe()!;
  const admin = opts.admin;
  let existing: (EntryDTO & { error?: string | null }) | null = preset ?? (id ? await findLocal(id) : null);
  if (!preset && existing && navigator.onLine) {
    // Vor dem Bearbeiten den aktuellen Serverstand holen, damit nicht auf einem veralteten Cache gearbeitet wird.
    await refreshMonth(monthOf(existing.date)).catch(() => null);
    existing = (await findLocal(existing.id)) ?? existing;
  }
  if (id && !existing) {
    el.innerHTML = '<p>Eintrag nicht gefunden.</p><a class="btn" href="#/eintraege">Zurück</a>';
    return;
  }
  const today = berlinDate(Date.now());
  const date = existing?.date ?? today;
  const locked = !admin && existing ? (await localMonth(monthOf(existing.date))).locked : false;
  const activities = admin?.activities ?? me.activities;
  const running = existing && existing.end === null;
  const defaultActivity = existing?.activity ?? localStorage.getItem('ze:lastActivity') ?? activities[0] ?? '';

  el.innerHTML = `
    <h1>${existing ? 'Eintrag bearbeiten' : 'Zeit nachtragen'}</h1>
    ${admin ? `<p class="muted">für ${esc(admin.userName)}</p>` : ''}
    ${existing?.error ? `<div class="alert error">Vom Server abgelehnt: ${esc(existing.error)}</div>` : ''}
    ${locked ? '<div class="alert">Dieser Monat ist freigegeben. Änderungen bitte bei der Verwaltung melden.</div>' : ''}
    ${running ? '<div class="alert">Diese Stempelung läuft noch. Zum Beenden auf der Startseite „Gehen" drücken oder hier ein Ende eintragen.</div>' : ''}
    <form id="entry" class="stack" ${locked ? 'inert' : ''}>
      <label>Datum<input type="date" name="date" value="${esc(date)}" ${admin ? '' : `max="${today}"`} required></label>
      <div class="row2">
        <label>Beginn<input type="time" name="start" value="${esc(existing?.start ?? '')}" required></label>
        <label>Ende<input type="time" name="end" value="${esc(existing?.end ?? '')}" ${running ? '' : 'required'}></label>
      </div>
      <label>Pause (Minuten)<input type="number" name="pauseMin" min="0" step="1" inputmode="numeric" value="${existing?.pauseMin ?? 0}"></label>
      <label>Tätigkeit / Objekt<select name="activity">${options(activities, defaultActivity)}</select></label>
      <label>Notiz<textarea name="note" rows="2" maxlength="1000">${esc(existing?.note ?? '')}</textarea></label>
      <div id="info" class="info" aria-live="polite"></div>
      <button class="btn primary big" id="save">Speichern</button>
      ${existing ? '<button type="button" class="btn danger" id="delete">Eintrag löschen</button>' : ''}
      ${existing?.error ? '<button type="button" class="btn" id="discard">Änderung verwerfen</button>' : ''}
    </form>
    ${admin?.audit ? auditHtml(admin.audit as AuditItem[]) : ''}
  `;
  const form = $('#entry', el) as HTMLFormElement;

  const read = (): EntryChange | null => {
    const v = formValues(form);
    if (!isValidDate(v.date) || !isValidTime(v.start) || (v.end && !isValidTime(v.end))) return null;
    const now = Date.now();
    return {
      id: existing?.id ?? newId(),
      userId: admin?.userId ?? existing?.userId,
      date: v.date,
      start: v.start,
      end: v.end || null,
      pauseMin: Math.max(0, Math.round(Number(v.pauseMin) || 0)),
      pauseStart: v.end ? null : existing?.pauseStart ?? null,
      activity: v.activity,
      note: v.note.trim(),
      source: existing?.source ?? 'manual',
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
      deleted: false,
      baseUpdatedAt: existing ? existing.updatedAt : null,
    };
  };

  const check = async (): Promise<{ errors: string[]; warnings: string[] }> => {
    const c = read();
    const info = $('#info', el);
    if (!c) {
      info.innerHTML = '';
      return { errors: ['Bitte Datum, Beginn und Ende ausfüllen.'], warnings: [] };
    }
    const v = validateEntry(c);
    const notes: string[] = [];
    if (!v.errors.length && c.end) {
      const iv = entryInterval(c.date, c.start, c.end);
      notes.push(`Arbeitszeit: <strong>${formatHM(workMinutes(c.date, c.start, c.end, c.pauseMin))} Std.</strong>`);
      if (iv.overnight) notes.push('Endet am Folgetag (über Mitternacht).');
    }
    if (!admin) {
      const others = (await localAround(c.date)).filter((o) => !o.deleted);
      const clash = findOverlaps(c, others);
      if (clash.length) v.errors.push(`Überschneidung mit ${dayLabel(clash[0].date)} ${clash[0].start}–${clash[0].end ?? 'läuft'}.`);
      if (!existing && daysBetween(c.date, today) > MILOG_DAYS) {
        v.warnings.push('Nachtrag später als 7 Tage nach dem Arbeitstag – wird für die Verwaltung markiert (§ 17 MiLoG).');
      }
    }
    info.innerHTML = [
      ...notes.map((n) => `<p>${n}</p>`),
      ...v.warnings.map((w) => `<p class="warn">${esc(w)}</p>`),
      ...v.errors.map((e) => `<p class="form-error">${esc(e)}</p>`),
    ].join('');
    return v;
  };
  form.addEventListener('input', () => void check());
  void check();

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const v = await check();
    const c = read();
    if (!c || v.errors.length) {
      toast(v.errors[0] ?? 'Bitte Eingaben prüfen.', 'error');
      return;
    }
    if (admin) {
      await adminSave(c, admin.onDone);
      return;
    }
    localStorage.setItem('ze:lastActivity', c.activity);
    const clock = await getClock();
    if (clock && clock.id === c.id) await setClock(c.end ? null : c);
    await saveChange(c);
    toast(navigator.onLine ? 'Gespeichert.' : 'Gespeichert – wird hochgeladen, sobald Netz da ist.');
    history.back();
  });

  document.getElementById('delete')?.addEventListener('click', async () => {
    if (!existing || !confirm('Diesen Eintrag wirklich löschen?')) return;
    const c: EntryChange = {
      ...toChange({ ...existing, late: false } as EntryDTO), deleted: true, updatedAt: Date.now(), baseUpdatedAt: existing.updatedAt,
    };
    if (admin) {
      await adminSave(c, admin.onDone);
      return;
    }
    const clock = await getClock();
    if (clock && clock.id === c.id) await setClock(null);
    await saveChange(c);
    toast('Gelöscht.');
    history.back();
  });

  document.getElementById('discard')?.addEventListener('click', async () => {
    if (!existing) return;
    await outbox.discard(existing.id);
    toast('Lokale Änderung verworfen.');
    history.back();
  });
}

async function adminSave(c: EntryChange, onDone: () => void): Promise<void> {
  try {
    const r = await api<{ results: SyncResult[] }>('/sync', { method: 'POST', body: { entries: [c] } });
    const res = r.results[0];
    if (res.status === 'ok') {
      toast(res.warnings?.length ? `Gespeichert. Hinweis: ${res.warnings[0]}` : 'Gespeichert.', res.warnings?.length ? 'warn' : 'ok');
      onDone();
    } else {
      toast(res.error ?? 'Konflikt: Der Eintrag wurde zwischenzeitlich geändert. Bitte neu laden.', 'error');
    }
  } catch (err) {
    toast(errorText(err), 'error');
  }
}

interface AuditItem {
  at: number;
  actorName: string | null;
  action: string;
  before: Partial<EntryDTO> | null;
  after: Partial<EntryDTO> | null;
}

const FIELDS: [keyof EntryDTO, string][] = [
  ['date', 'Datum'], ['start', 'Beginn'], ['end', 'Ende'], ['pauseMin', 'Pause'], ['activity', 'Tätigkeit'], ['note', 'Notiz'], ['deleted', 'gelöscht'],
];

function auditHtml(list: AuditItem[]): string {
  const label: Record<string, string> = { create: 'angelegt', update: 'geändert', delete: 'gelöscht' };
  return `<section class="audit"><h2>Änderungsprotokoll</h2><ol>${list
    .map((a) => {
      const changes = a.before && a.after
        ? FIELDS.filter(([k]) => a.before![k] !== a.after![k])
          .map(([k, l]) => `${l}: ${esc(String(a.before![k] ?? '–'))} → ${esc(String(a.after![k] ?? '–'))}`)
          .join('<br>')
        : '';
      return `<li><strong>${esc(new Date(a.at).toLocaleString('de-DE'))}</strong> · ${esc(a.actorName ?? 'System')} · ${esc(label[a.action] ?? a.action)}
        ${changes ? `<div class="small">${changes}</div>` : ''}</li>`;
    })
    .join('')}</ol></section>`;
}
